import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import isEmpty from 'lodash.isempty';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotificationStore } from '../../../components/notificationStore';
import { useUserStore } from '../../../components/userStore';
import { formatBirthDate, formatPhoneNumber } from '../../../components/util';
import constants from '../../../constants';
import { useApplicationStore } from './applicationStore';

const ConfirmChoices = () => {
	const { user } = useUserStore();
	const navigate = useNavigate();
	const {
		getClient,
		getApplication,
		taxonomyDetail,
		program,
		reset,
		setClientId,
		textConsent,
		setTextConsent,
		emailConsent,
		setEmailConsent,
	} = useApplicationStore();
	const queryClient = useQueryClient();
	const [hasVerbalConsent, setVerbalConsent] = useState(false);
	const { showSuccess, showError } = useNotificationStore();

	const mutation = useMutation({
		mutationFn: async (serviceRequest) => {
			const { data } = await axios.post(
				'/requests/submit-application',
				serviceRequest,
			);
			if (data.statusCode === 422) {
				throw new Error('Application submission failed');
			}
			return data;
		},
		onSuccess: (data) => {
			queryClient.invalidateQueries({
				queryKey: ['dashboard'],
			});
			queryClient.invalidateQueries({
				queryKey: ['worklist'],
			});
			queryClient.invalidateQueries({
				queryKey: ['menu-counts'],
			});
			showSuccess('Application submitted successfully!');
			setClientId(data.client_id);
		},
		onError: (error) => {
			showError(
				`There was a problem submitting application! ${error?.message}`,
			);
		},
	});

	const handleSubmit = (event) => {
		event.preventDefault();
		mutation.mutate(getApplication());
	};

	const handleReturnToWorklist = () => {
		navigate('/');
		reset();
	};

	const handleOpenApplication = () => {
		navigate(`/worklist/application/${mutation.data.id}`);
		reset();
	};

	const handleCloneApplication = () => {
		navigate('/newApplication/search');
	};

	const client = getClient();
	const isTandemOrgService = !!program?.ccAttributes?.coord_agency_id;
	const acceptsElectronicReferralsTag = program?.tags.find(
		({ category }) => category === 'accepts_e_ref',
	);
	const acceptsElectronicReferralsCurrently =
		acceptsElectronicReferralsTag?.properties.find(
			({ name }) => name === 'accepts_e_ref_currently',
		)?.value === '1';

	return (
		<div className="confirm-choices">
			<h1>Confirm Choices</h1>
			<div className="mt-6">
				{mutation.isPending && <div>Submitting application...</div>}
				<div>
					<h3>
						Applicant Information
						{client.id && ` (Existing Profile)`}
					</h3>
				</div>
				<div className="mt-4">
					<dl>
						<div>
							<dt>Full name</dt>
							<dd>
								{client.name_first} {client.name_last}
							</dd>
						</div>
						<div>
							<dt>Email</dt>
							<dd>
								{isEmpty(client.email) ? <em>Not entered</em> : client.email}
							</dd>
						</div>
						<div>
							<dt>Mobile phone</dt>
							<dd>
								{isEmpty(client.phone_mobile) ? (
									<em>Not entered</em>
								) : (
									formatPhoneNumber(client.phone_mobile)
								)}
							</dd>
						</div>
						<div className="col-span-2">
							<dt>Address</dt>
							<dd>
								{isEmpty(client.address) ? (
									<em>Not entered</em>
								) : (
									client.address
								)}
							</dd>
						</div>
						<div>
							<dt>Date of birth</dt>
							<dd>{formatBirthDate(client.birth_dttm)}</dd>
						</div>
						<div>
							<dt>City</dt>
							<dd>
								{isEmpty(client.city) ? <em>Not entered</em> : client.city}
							</dd>
						</div>
						<div>
							<dt>State</dt>
							<dd>
								{isEmpty(client.state) ? <em>Not entered</em> : client.state}
							</dd>
						</div>
						<div>
							<dt>Zip</dt>
							<dd>{isEmpty(client.zip) ? <em>Not entered</em> : client.zip}</dd>
						</div>
						<div>
							<dt>Marital Status</dt>
							<dd>
								{isEmpty(client.marital_status) ? (
									<em>Not entered</em>
								) : (
									constants.marital_status[client.marital_status]
								)}
							</dd>
						</div>
						<div>
							<dt>Disability</dt>
							<dd>
								{isEmpty(client.disability_status) ? (
									<em>Not entered</em>
								) : (
									constants.disability[client.disability_status]
								)}
							</dd>
						</div>
						<div>
							<dt>Veteran</dt>
							<dd>
								{isEmpty(client.veteran_status) ? (
									<em>Not entered</em>
								) : (
									constants.veteran[client.veteran_status]
								)}
							</dd>
						</div>
						<div>
							<dt>Ethnicity</dt>
							<dd>
								{isEmpty(client.ethnicity) ? (
									<em>Not entered</em>
								) : (
									constants.ethnicity[client.ethnicity]
								)}
							</dd>
						</div>
						<div>
							<dt>Race</dt>
							<dd>
								{isEmpty(client.race) ? (
									<em>Not entered</em>
								) : (
									constants.race[client.race]
								)}
							</dd>
						</div>
						<div>
							<dt>4 digit verification number</dt>
							<dd>
								{isEmpty(client.last_4_ssn) ? <em>Not entered</em> : '****'}
							</dd>
						</div>
					</dl>
				</div>
				<div className="pt-6 border-t border-gray-200">
					<h3>Requested Service Provider</h3>
				</div>
				<p className="px-4 py-6">
					By requesting this service(s), I consent to the{' '}
					<a
						target="_blank"
						href="https://nowintandem.com/terms_of_use/"
						rel="noreferrer"
						className="link"
					>
						Terms of Use
					</a>
					, the{' '}
					<a
						target="_blank"
						href="https://nowintandem.com/privacy_policy/"
						rel="noreferrer"
						className="link"
					>
						Privacy Policy
					</a>{' '}
					and to sharing my information with the following organizations.
				</p>
				<div className="mt-4">
					<dl className="grid-cols-3">
						<div>
							<dt>Program</dt>
							<dd>{taxonomyDetail || program?.name}</dd>
						</div>
						<div>
							<dt>Organization</dt>
							<dd>{program.locations[0].organization_name}</dd>
						</div>
					</dl>
				</div>
			</div>
			{mutation.isSuccess && (
				<div className="text-center">
					<button
						className="btn btn-primary btn-wide"
						onClick={handleReturnToWorklist}
					>
						Return to the dashboard
					</button>
					<button
						className="ml-4 btn btn-primary btn-wide"
						onClick={handleCloneApplication}
					>
						Add Another Service
					</button>
					<button
						className="ml-4 btn btn-primary btn-wide"
						onClick={handleOpenApplication}
					>
						Open Application
					</button>
				</div>
			)}
			{(mutation.isError || mutation.isPending || mutation.isIdle) && (
				<>
					<div className="mb-4 text-sm">
						{acceptsElectronicReferralsCurrently || isTandemOrgService ? (
							<>
								<p className="mb-3">
									<strong>Our purpose:</strong> The{' '}
									<strong>{user.coord_agency?.hubs[0].hub_name}</strong> was
									created to support connections to social need services in a
									manner that: (1) reduces barriers to accessing services, (2)
									protects privacy of personal information, and (3) strengthen
									the <strong>{user.coord_agency?.hubs[0].hub_name}</strong>{' '}
									social care safety net.
								</p>
								<p className="mb-3">
									<strong>Our commitment to you:</strong> We are committed to
									ensuring that you control your personal data. You own all data
									in your profile and can modify or delete your profile at any
									time. Identifiable data is only shared upon your request when
									you apply for services. After you apply for a service, the
									only information shared with the referring agency is the
									status and time of status changes your request.
								</p>
								<p>
									<strong>Consent to share data:</strong> Today, you are
									applying for <strong>{taxonomyDetail}</strong> from{' '}
									<strong>{program.locations[0].organization_name}</strong>,
									which requires sharing of the following data:
								</p>
								<ul className="ml-6 list-disc">
									<li className="my-2">
										To <strong>{program.locations[0].organization_name}</strong>
										, for the purposes of determining your eligibility for the
										service: Your contact information, account ID, name of
										referring agency if applicable, application information
										necessary to determine eligibility, which may include the
										name of any service-related funding source.
									</li>
									<li className="my-2">
										To <strong>{user.coord_agency.agency_name}</strong>, if
										applicable, for the purpose of ensuring services are
										received: Service fulfillment status and dates of status
										changes, along with your name, account ID, and contact
										information.
									</li>
									<li className="my-2">
										To <strong>{user.coord_agency?.hubs[0].entity_name}</strong>{' '}
										for the purposes of CIE intermediary administration,
										providing additional support if needed, and strengthening of
										the social care safety-net: First and last name, date of
										birth, your phone number, your account ID, service agency
										name, referring agency name, service request, service status
										changes and dates.
									</li>
								</ul>
								<p className="mb-3">
									I hereby request and authorize the exchange of data, as
									described above, for the purposes described. In doing so, I am
									aware that I may, without penalty, refuse consent for the
									exchange of information by any agencies listed. I understand
									that I can seek additional information about my data, how it
									is protected, and how it is shared at any time by contacting{' '}
									<strong>
										<a
											target="_blank"
											href={user.coord_agency?.hubs[0].hub_org_url}
											rel="noreferrer"
											className="link"
										>
											{user.coord_agency?.hubs[0].hub_org_url}
										</a>
									</strong>
									.
								</p>
								<div className="pl-4 mb-8 form-control">
									<label className="gap-4 cursor-pointer label justify-normal">
										<input
											type="checkbox"
											className="checkbox"
											checked={hasVerbalConsent}
											disabled={mutation.isPending}
											onChange={({ target }) =>
												setVerbalConsent(target.checked)
											}
										/>
										<span className="label-text">Verbal Consent</span>
									</label>
								</div>
							</>
						) : (
							<>
								<p className="mb-3">
									<strong>Our purpose:</strong> The{' '}
									<strong>{user.coord_agency?.hubs[0].hub_name}</strong> was
									created to support connections to social need services in a
									manner that: (1) reduces barriers to accessing services, (2)
									protects privacy of personal information, and (3) strengthen
									the <strong>{user.coord_agency?.hubs[0].hub_name}</strong>{' '}
									social care safety net.
								</p>
								<p className="mb-3">
									<strong>Our commitment to you:</strong> We are committed to
									ensuring that you control your personal data. You own all data
									in your profile and can modify or delete your profile at any
									time. Identifiable data is only shared upon your request when
									you apply for services. After you apply for a service, the
									only information shared with the referring agency is the
									status and time of status changes of your request.
								</p>
								<p>
									<strong>Consent to share data:</strong> Today, we are
									providing you with contact information to request{' '}
									<strong>{taxonomyDetail}</strong> from{' '}
									<strong>{program.locations[0].organization_name}</strong>. To
									help us best connect you to resources, we request permission
									to share the following information:
								</p>
								<ul className="ml-6 list-disc">
									<li className="my-2">
										With <strong>{user.coord_agency.agency_name}</strong>, for
										their recordkeeping. Information shared may include your
										name, contact information, account number, and referral
										information.
									</li>
									<li className="my-2">
										With{' '}
										<strong>{user.coord_agency?.hubs[0].entity_name}</strong>,
										to support community service improvement. Information shared
										may include your name, date of birth, phone number, account
										ID, the organizations involved in the referral, the service
										requested, and updates about the referral.
									</li>
								</ul>
								<p className="mb-3">
									I request and authorize the exchange of information as
									described above for the purposes stated. I understand that I
									may refuse consent for the exchange of information with any of
									the agencies listed without penalty. I may also request
									additional information about how my data is collected, shared,
									and protected at any time by contacting{' '}
									<strong>
										<a
											target="_blank"
											href={user.coord_agency?.hubs[0].hub_org_url}
											rel="noreferrer"
											className="link"
										>
											{user.coord_agency?.hubs[0].hub_org_url}
										</a>
									</strong>
								</p>
								<div className="pl-4 mb-8 form-control">
									<label className="gap-4 cursor-pointer label justify-normal">
										<input
											type="checkbox"
											className="checkbox"
											checked={hasVerbalConsent}
											disabled={mutation.isPending}
											onChange={({ target }) =>
												setVerbalConsent(target.checked)
											}
										/>
										<span className="label-text">Verbal Consent</span>
									</label>
								</div>
								{!isEmpty(client.phone_mobile) && (
									<div>
										<p>
											<strong>Consent to Receive Text Messages</strong>
										</p>
										<p className="mb-3">
											May we send text messages to{' '}
											{formatPhoneNumber(client.phone_mobile)} with updates
											about your referral to {user.coord_agency.agency_name},
											for {taxonomyDetail}?
										</p>
										<div className="px-4 form-control">
											<label className="gap-4 cursor-pointer label justify-normal">
												<input
													type="radio"
													name="text-consent"
													className="radio"
													checked={textConsent}
													disabled={mutation.isPending}
													onChange={() => setTextConsent(true)}
												/>
												<span className="label-text">
													YES — I agree to receive text messages at this number
												</span>
											</label>
										</div>
										<div className="px-4 form-control">
											<label className="gap-4 cursor-pointer label justify-normal">
												<input
													type="radio"
													name="text-consent"
													className="radio"
													checked={!textConsent}
													disabled={mutation.isPending}
													onChange={() => setTextConsent(false)}
												/>
												<span className="label-text">
													NO - I do not agree to receive text messages
												</span>
											</label>
										</div>
									</div>
								)}
								{!isEmpty(client.email) && (
									<div>
										<p>
											<strong>Consent to Receive Email Messages</strong>
										</p>
										<p className="mb-3">
											May we send email messages to {client.email} with updates
											about your referral to {user.coord_agency.agency_name},
											for {taxonomyDetail}?
										</p>
										<div className="px-4 form-control">
											<label className="gap-4 cursor-pointer label justify-normal">
												<input
													type="radio"
													name="email-consent"
													className="radio"
													checked={emailConsent}
													disabled={mutation.isPending}
													onChange={() => setEmailConsent(true)}
												/>
												<span className="label-text">
													YES — I agree to receive email messages at this
													address
												</span>
											</label>
										</div>
										<div className="px-4 form-control">
											<label className="gap-4 cursor-pointer label justify-normal">
												<input
													type="radio"
													name="email-consent"
													className="radio"
													checked={!emailConsent}
													disabled={mutation.isPending}
													onChange={() => setEmailConsent(false)}
												/>
												<span className="label-text">
													NO - I do not agree to receive email messages
												</span>
											</label>
										</div>
									</div>
								)}
							</>
						)}
					</div>
					<div className="grid grid-cols-2">
						<div></div>
						<div className="flex justify-end">
							<button
								className="btn btn-primary btn-wide"
								disabled={!hasVerbalConsent || mutation.isPending}
								onClick={handleSubmit}
							>
								Submit Application
							</button>
						</div>
					</div>
				</>
			)}
		</div>
	);
};

export default ConfirmChoices;
